import { html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import './soccer-league-detail';
import { connect } from '../store/connect';
import { selectors } from '../site/';
import { FormElementSPLeagues, LIMIT_TYPE } from './form-element-sp-leagues';
import { SOCCER_AUTH_DEFS } from './constants';
const limitOptions = [
    {
        name: LIMIT_TYPE.NONE,
        displayKey: 'All Authorized Seasons',
    },
    {
        name: LIMIT_TYPE.ALLOW,
        displayKey: 'Select Seasons',
    },
];

class FormElementSoccerLeagues extends FormElementSPLeagues {
    constructor() {
        super();
        this.siteLevelAuths = {};
    }

    static get styles() {
        return [
            super.styles,
            css`
                soccer-league-detail {
                    background-color: var(--app-surface-color);
                    margin-top: 8px;
                    margin-bottom: 8px;
                    padding-top: 8px;
                    padding-bottom: 8px;
                    border: 1px solid #0003;
                }
                tmn-select {
                    display: inline-block;
                    margin-left: 12px;
                }
                .data-option-container {
                    border-bottom: 1px solid var(--app-secondary-color);
                    padding-bottom: 8px;
                    margin-bottom: 8px;
                }
            `,
        ];
    }

    static get properties() {
        return {
            siteLevelAuths: {
                type: Object,
            },
        };
    }

    firstUpdated() {
        super.firstUpdated();
        this.addEventListener('soccer-season-auth-changed', this.handleSeasonAuthChanged);
    }

    renderEditHeader() {
        return html` <div class="data-option-container">
                <h3>Data Options</h3>
                <div>${this.renderDataOptions()}</div>
            </div>
            <div class="header-container">
                <h3>League Options</h3>

                ${this.renderManageLeaguesChooser()}
            </div>`;
    }

    renderDataOptions() {
        const { value, siteLevelAuths = {} } = this;
        const { limitType, leagues, ...seasonAuths } = value ?? {};
        const allowedfilteredDefs = SOCCER_AUTH_DEFS.filter(
            ({ siteLevelAuth }) => siteLevelAuths[siteLevelAuth]
        );
        return allowedfilteredDefs.map(
            ({ auth, label }) => html`${this.renderLimitOptions(auth, label)}`
        );
    }

    renderLimitOptions(auth, label) {
        const { limitType } = this.getAuthDetails(auth);
        const selection = limitOptions.filter((s) => s.name === limitType);
        return html`
            <tmn-select
                class="limit-select"
                .label=${label}
                .placeholder=${'Select Mode'}
                @tmn-selections-changed=${this.handleLimitTypeChanged}
                .selections=${selection}
                .data=${limitOptions}
                .name=${auth}
                nosearch></tmn-select>
        `;
    }

    getAuthDetails(auth) {
        const { value } = this;
        const { limitType: leagueLimitType, leagues, ...seasonAuths } = value ?? {};
        const { authSeasons = [], limitType } = seasonAuths[auth] ?? {};
        return { authSeasons, limitType };
    }

    handleLimitTypeChanged(e) {
        const { path, value } = this;
        const { target, detail } = e;
        const auth = target.name;
        const { selections } = detail;
        const { name: newLimitType } = selections[0];
        const { authSeasons = [] } = this.getAuthDetails(auth);
        const newValue = {
            ...value,
            [auth]: {
                limitType: newLimitType,
                authSeasons,
            },
        };
        this.actions.setStagedEditByPath(path, newValue);
    }

    renderLeagueDetail(leagueDetail, selectedValues) {
        const { seasonAuths } = selectedValues;
        const viewMode = this.isViewMode();
        const {
            availableSeasons,
            limitType = LIMIT_TYPE.NONE,
            seasons,
            displayKey,
            leagueId,
        } = leagueDetail;
        const detailClasses = classMap({
            'deny-mode': limitType === LIMIT_TYPE.DENY,
        });
        return html`<soccer-league-detail
            class=${detailClasses}
            .leagueId=${leagueId}
            .displayKey=${displayKey}
            .seasons=${seasons}
            .limitType=${limitType}
            .isViewMode=${viewMode}
            .seasonAuths=${seasonAuths}
            .availableSeasons=${availableSeasons}></soccer-league-detail>`;
    }

    getSelectedValues() {
        const { value, availableLeagues = [] } = this;
        const { limitType = LIMIT_TYPE.ALLOW, leagues = [], ...seasonAuths } = value ?? {};
        // todo should move these to selectors?
        const selectedLeaguesData = leagues
            .map((l) => {
                // to get all possible seasons for given league
                const availableLeagueData = availableLeagues.find((al) => al.name === l.leagueId);
                if (!availableLeagueData) {
                    // this plus the filter below accounts for cases where there is bad data in the saved leagues for a client
                    // (i.e. for some reason the league saved is no longer in availableLeageData)
                    return;
                }
                return {
                    ...l,
                    // combining it all this way may be a bad idea..
                    displayKey: availableLeagueData?.displayKey,
                    leagueId: availableLeagueData?.name,
                    availableSeasons: availableLeagueData?.seasons,
                };
            })
            .filter((v) => v);
        // note this is a little weird b/c when you first add a league it would be nice if it stayed at the top and
        // did not get sorted, we could implement that but would take a little time
        if (selectedLeaguesData?.length) {
            selectedLeaguesData.sort((a, b) => {
                const nameA = a.displayKey.toUpperCase(); // ignore upper and lowercase
                const nameB = b.displayKey.toUpperCase(); // ignore upper and lowercase
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }

                // names must be equal
                return 0;
            });
        }

        return {
            limitType,
            selectedLeaguesData,
            seasonAuths,
        };
    }

    handleSeasonAuthChanged(e) {
        const { path, value } = this;
        const { detail } = e;
        const { leagueId, ...seasonAuths } = detail;
        const newValue = {
            ...value,
            ...seasonAuths,
        };
        this.actions.setStagedEditByPath(path, newValue);
    }
}

const mapStateToProps = (state, ownProps) => {
    return (state) => {
        return {
            availableLeagues: selectors.availableLeaguesSelector(state),
            siteLevelAuths: selectors.activeRecordSiteAuths(state),
        };
    };
};

window.customElements.define(
    'form-element-soccer-leagues',
    connect(mapStateToProps)(FormElementSoccerLeagues)
);
